import UAParser from 'ua-parser-js';
import { format } from 'date-fns';
import NoImageSVG from 'components/views/vod/images/placeholder.svg';
import getSlug from 'speakingurl';
import { isObject } from 'lodash';

// Import helpers
import { convertDatesToBroadcastHours } from 'helpers/time';
import {
	APP_PROJECT_TYPES,
	PLAYER_SELECTORS,
	ROUTES_CORE_PATHS,
	POSTAL_CODES_REGEX,
	IMAGES_TYPES,
	PRODUCT_IMAGES_TYPES,
	PRODUCTS_TYPES
} from 'helpers/variables';

// Import StyledBorderButton styles
import {
	backgroundHovered,
	borderHovered,
	smartBackgroundHovered,
	smartBorderHovered
} from 'components/styles';

const { POSTER, LOGO } = IMAGES_TYPES;
const { CHANNEL } = PRODUCTS_TYPES;

// Get user agent
export const getUserAgent = () => {
	const uastring = navigator.userAgent;
	const parser = new UAParser();
	const { browser, os } = parser.setUA(uastring).getResult();
	return { browser, os };
};

// Convert currency
export const convertCurrencyFormat = (price) => price / 100;

export const createNavList = (categories, slugAll = '') => [
	{ slug: slugAll, name: 'Wszystkie', id: 'all' },
	{ slug: 'ulubione', name: 'Ulubione', id: 'favourite' },
	...categories
];
export const filterNavList = ({
	navList,
	isAuth = true,
	id = 'favourite'
} = {}) =>
	createNavList(navList).filter((item) =>
		item.id === id ? isAuth && item : item
	);

// Set channel logo
export const setChannelLogo = (images) => {
	if (images && images.logo && images.logo.length > 0)
		return images.logo[0].url;
	else if (images && images.logo_inverted.length > 0)
		return images.logo_inverted[0].url;
};

export const setChannelInverseLogo = (image) => {
	let imgUrl = '';
	let isImgUrlValid = true;

	if (image && image.logo_inverted && image.logo_inverted.length > 0) {
		imgUrl = image.logo_inverted[0].url;
	} else if (image && image.logo && image.logo.length > 0) {
		imgUrl = image.logo[0].url;
	} else {
		isImgUrlValid = false;
	}

	return { imgUrl, isImgUrlValid };
};

// Set image url
export const setImgUrl = (url) => {
	if (url && url.match(/^(https:\/\/)/)) {
		return url.split('?')[0];
	} else if (url && url.match(/^(http:\/\/)/)) {
		return url.split('?')[0];
	} else if (url) {
		return `https:${url.split('?')[0]}`;
	}
};

// Check preview img url
export const getImgUrl = ({
	images,
	width = 400,
	height = 350,
	isLive = true,
	onlyCover = false
}) => {
	let imgUrl = '';
	let isImgUrlValid = true;

	if (images && images.cover && images.cover.length > 0) {
		imgUrl = images.cover[0].url;
	} else if (
		!onlyCover &&
		images &&
		images.slides &&
		images.slides.length > 0 &&
		isLive
	) {
		imgUrl = `${setImgUrl(images.slides[0].url)}${setImgSizePath(
			width,
			height
		)}`;
	} else if (!onlyCover && images && images.logo && images.logo.length > 0) {
		imgUrl = images.logo[0].url;
	} else {
		isImgUrlValid = false;
	}

	return { imgUrl: imgUrl, isImgUrlValid };
};

export const checkPosterUrl = ({
	images,
	slides,
	width = 400,
	height = 350
}) => {
	let imgUrl = '';
	let isImgUrlValid = true;

	if (images && images.poster && images.poster.length > 0) {
		imgUrl = images.poster[0].url;
	} else if (images && images.cover && images.cover.length > 0) {
		imgUrl = images.cover[0].url;
	} else if (slides) {
		imgUrl = `${setImgUrl(slides)}${setImgSizePath(width, height)}`;
	} else {
		isImgUrlValid = false;
	}

	return { imgUrl, isImgUrlValid };
};

/**
 * returns poster image or error image
 * @param {object} images
 * @return {*}
 */
export function getPosterImage(images, subType) {
	const imagesType = subType === CHANNEL ? LOGO : POSTER;

	if (
		typeof images === 'object' &&
		!!images &&
		images[imagesType] &&
		images[imagesType].length > 0 &&
		!!images[imagesType][0].url
	) {
		return images[imagesType][0].url;
	}

	return NoImageSVG;
}

// Create image sizePath
export const setImgSizePath = (width = 400, height = 350) =>
	`?srcmode=4&srcw=${width}&srch=${height}&dstw=${width}&dsth=${height}&type=1&quality=80`;

// CreateLink params
export const createLinkParams = ({
	id,
	hash = 'program',
	type = 'program',
	title = ''
}) => {
	const slugTitle = getSlug(title);
	const typeName = getTypeTranslation(type);
	const { TELEVISION } = ROUTES_CORE_PATHS;

	switch (type) {
		case 'channel':
			return {
				pathname: `/tv/${TELEVISION}`,
				state: { channelId: id }
			};

		case 'vod':
			return {
				pathname: `/vod/filmy/${id}/${slugTitle}`
			};

		case 'series':
			return {
				pathname: `/vod/${typeName}/${id}/${slugTitle}`
			};

		case 'season':
			return {
				pathname: `/vod/${typeName}/${id}/${slugTitle}`
			};

		case 'episode':
			return {
				pathname: `/vod/${typeName}/${id}/${slugTitle}`
			};

		case 'provision':
			return {
				pathname: `/pakiety/pakiet/${id}/${slugTitle}`
			};

		case 'collection':
			return {
				pathname: `/vod/${typeName}/${id}/${slugTitle}`
			};

		case 'main':
			return {
				pathname: `/pakiety/pakiet/${id}/${slugTitle}`
			};

		// program
		default:
			return {
				pathname: `/tv/kanaly/szczegoly-programu/${id}`,
				hash
			};
	}
};

// Get search parametr
export const getSearchParametr = (search, paramName) => {
	const searchParams = new URLSearchParams(search);
	return searchParams.get(paramName) || false;
};

// Set search parametr
export const setParametr = (name, value = '') => {
	const searchParams = new URLSearchParams();
	searchParams.set(name, value);
	return searchParams.toString();
};

// required types to translate links
export const urlTranslationTypes = {
	regulamin: 'regulation',
	regulation: 'regulamin',
	'o-nas': 'about-us',
	'about-us': 'o-nas',
	strona: 'page',
	page: 'strona',
	logowanie: 'login',
	login: 'logowanie',
	home: 'strona-glowna',
	'strona-glowna': 'home',
	kanaly: 'channels',
	channels: 'kanaly',
	'szczegoly-programu': 'program-details',
	'program-details': 'szczegoly-programu',
	pakiety: 'packets',
	packets: 'pakiety',
	filmy: 'vod',
	movies: 'filmy',
	seriale: 'series',
	series: 'seriale',
	'dla-dzieci': 'kids',
	kids: 'dla-dzieci',
	kolekcje: 'collection',
	collection: 'kolekcje',
	packet: 'pakiet',
	pakiet: 'packet',
	main: 'pakiet',
	vod: 'vod',
	epg: 'epg',
	telewizja: 'television',
	sekcja: 'section',
	section: 'sekcja',
	szukaj: 'search',
	search: 'szukaj',
	wszystkie: 'all',
	all: 'wszystkie',
	program: 'programy',
	user: 'uzytkownik',
	uzytkownik: 'user',
	'historia-platnosci': 'payments',
	payments: 'historia-platnosci',
	profil: 'general',
	general: 'profil',
	urzadzenia: 'devices',
	devices: 'urzadzenia',
	nagrania: 'recordings',
	recordings: 'nagrania',
	'aktywne-wypozyczenia': 'products',
	products: 'aktywne-wypozyczenia',
	ulubione: 'favourites',
	favourites: 'ulubione',
	programs: 'programy',
	programy: 'programs',
	movie: 'film',
	film: 'movie',
	odcinek: 'episode',
	episode: 'odcinek',
	sezon: 'season',
	season: 'sezon',
	rodo: 'rodo',
	faq: 'faq',
	'metoda-platnosci': 'payment_method',
	payment_method: 'metoda-platnosci',
	'poznaj-tv-smart': 'tvsmart',
	konfigurator: 'configurator',
	'poznaj-dekoder-tv-smart': 'decoder',
	dokumenty: 'documents',
	documents: 'dokumenty',
	'twoje-pakiety': 'your_products',
	your_products: 'twoje-pakiety',
	zgody: 'agreements',
	agreements: 'zgody',
	rejestracja: 'register',
	'reset-hasla': 'password_reset',
	'potwierdzenie-email': 'email_confirm'
};

// required categories to translate search links
const urlTranslationCategories = {
	kanaly: 'channel',
	channel: 'kanaly',
	all: 'wszystkie',
	wszystkie: 'all',
	seriale: 'series',
	series: 'seriale',
	filmy: 'vod',
	vod: 'filmy',
	program: 'programy',
	programy: 'program'
};

// get translated type
export const getTypeTranslation = (type) => urlTranslationTypes[type];

// get translated search category
export const getSearchCategory = (type) => urlTranslationCategories[type];

// check if the pin.length === 4 and contains only numbers
export const checkPinFormat = (pin) => {
	const isValid = pin.toString().length === 4 && pin.match(/^\d+$/);
	return isValid;
};

// get app name based on env file
export const getAppName = () => {
	const { TV_SMART, TV_ONLINE } = APP_PROJECT_TYPES;
	const appName =
		process.env.REACT_APP_PROJECT === TV_SMART ? TV_SMART : TV_ONLINE;

	return appName.toLowerCase();
};

// check if app type === tv smart
export const checkAppType = () => {
	const { TV_SMART } = APP_PROJECT_TYPES;
	const appName = getAppName();
	const isTvSmart = appName === TV_SMART.toLowerCase();

	return { isTvSmart, appName };
};

// get StyledBorderButton hover background gradient
export const getButtonHoverGradient = (background = null, border = null) => {
	const { isTvSmart } = checkAppType();

	const tvonlineBackground = background || backgroundHovered;
	const tvonlineBorder = border || borderHovered;

	const backgroundHover = isTvSmart
		? smartBackgroundHovered
		: tvonlineBackground;
	const borderHover = isTvSmart ? smartBorderHovered : tvonlineBorder;

	return { backgroundHover, borderHover };
};

// check player type based on url
export const checkPlayerType = (pathname) => {
	const { WATCH, PROGRAM_DETAILS, TELEVISION } = ROUTES_CORE_PATHS;
	const { PLAYER, DETAILS, VOD } = PLAYER_SELECTORS;

	if (pathname.includes(WATCH)) {
		return VOD;
	} else if (pathname.includes(PROGRAM_DETAILS)) {
		return DETAILS;
	} else if (pathname.includes(TELEVISION)) {
		return PLAYER;
	}

	return null;
};

// check if there is UUID in URL
export const containsUUID = (string) => {
	const regexID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
	return regexID.test(string);
};

// Find proper priceId by payment method in all payments list
export const getPriceIdByMethod = (paymentMethod, paymentOptions) => {
	// find payment
	const selectedPayment = paymentOptions.find(
		(payment) => payment.payment_method === paymentMethod
	);
	// return selected payment id
	return selectedPayment?.id;
};

// check if postal code is valid
export const checkPostalCodeValidate = ({ postal_code, regex }) => {
	return postal_code.match(regex);
};

export const checkPolishPostalCodeValidation = ({ postal_code }) => {
	const isValid = checkPostalCodeValidate({
		postal_code,
		regex: POSTAL_CODES_REGEX.POLAND
	});

	return isValid && postal_code.includes('-') && postal_code.length === 6;
};

export const callAllFns = (...fns) => (...args) =>
	fns.forEach((fn) => fn && fn(...args));

export const replaceStringChar = (string = '', values = {}) => {
	let newValue = string;

	Object.entries(values).forEach(([key, value]) => {
		newValue = newValue.replace(key, value);
	});

	return newValue;
};

export const getFormikRefData = async ({
	formRef,
	isTriggerValidation = true
}) => {
	const formik = formRef?.current;
	if (formik) {
		const { state, submitForm, getFormikComputedProps, setFieldError } = formik;

		// run submitForm to trigger in validation
		isTriggerValidation && (await submitForm());

		const { isValid } = getFormikComputedProps();

		return { values: state.values, isValid, setFieldError };
	}

	return {
		values: null,
		isValid: false
	};
};

export const trimObjectProperties = (objectToTrim) => {
	for (const key in objectToTrim) {
		if (isObject(objectToTrim[key])) {
			trimObjectProperties(objectToTrim[key]);
		} else if (objectToTrim[key]?.trim) {
			objectToTrim[key] = objectToTrim[key].trim();
		}
	}

	return objectToTrim;
};

export const checkValidURL = (url) => {
	try {
		return Boolean(new URL(url));
	} catch (e) {
		return false;
	}
};

export const parseRecordingItem = ({ item, channels, isReady }) => {
	if (!channels) return item;
	const {
		channel_uuid,
		since,
		till,
		program,
		id,
		duration,
		program_uuid,
		type
	} = item;

	const channel = channels.find((channel) => channel.uuid === channel_uuid);

	// Do not return recording of not existing on web channel
	if (!channel) return null;

	return {
		channelTitle: channel?.title,
		date: format(new Date(since), 'DD.MM.YYYY'),
		time: convertDatesToBroadcastHours(since, till),
		recordingName: program.title,
		recordingId: id,
		backgroundImage: getImage({
			images: program.images,
			types: [PRODUCT_IMAGES_TYPES.COVER]
		}),
		duration,
		status: isReady ? 'ready' : 'pending',
		url: createLinkParams({ id: program_uuid, hash: type })
	};
};

export const getImage = ({ images, types = [] }) => {
	if (!images) return '';

	return (
		types.map((type) => images[type]?.[0]?.url).find((image) => image) || ''
	);
};
